import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, UserPlus } from 'lucide-react';
import { TextInput } from '../inputs/TextInput';
import { Select } from '../inputs/Select';
import { Alert } from '../feedback/Alert';
import { TagSelector } from './TagSelector';
import type { GuestTag } from '../../types';

interface AddGuestModalProps {
  onClose: () => void;
  onAdd: (data: { inviteId?: number; name: string; email: string; tagIds?: number[] }) => Promise<void>;
  isAdding: boolean;
  inviteOptions?: { value: string; label: string }[];
  tags?: GuestTag[];
}

export function AddGuestModal({ onClose, onAdd, isAdding, inviteOptions, tags }: AddGuestModalProps) {
  const [selectedInviteId, setSelectedInviteId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (inviteOptions && !selectedInviteId) {
      setError('Please select an invite');
      return;
    }

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      await onAdd({
        inviteId: selectedInviteId ? Number(selectedInviteId) : undefined,
        name: name.trim(),
        email: email.trim(),
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add guest');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-background-card rounded-xl border border-border shadow-xl mx-4 max-h-[90dvh] flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <h2 className="text-base font-semibold text-text-primary">Add Guest</h2>
          <button onClick={onClose} data-builder="modal-add-guest-close" className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
            {inviteOptions && (
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Invite</label>
                <Select
                  options={inviteOptions}
                  value={selectedInviteId}
                  onChange={setSelectedInviteId}
                  placeholder="Select an invite..."
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Name *</label>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Email (optional)</label>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>

            {tags && (
              <TagSelector
                availableTags={tags}
                selectedTagIds={selectedTagIds}
                onChange={setSelectedTagIds}
                label="Tags"
              />
            )}

            {error && <Alert variant="error">{error}</Alert>}
          </div>

          <div className="shrink-0 flex gap-3 px-4 py-3 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              data-builder="modal-add-guest-cancel"
              className="flex-1 px-4 py-2 text-sm border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAdding}
              data-builder="modal-add-guest-submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isAdding ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              {isAdding ? 'Adding...' : 'Add Guest'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
